import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { MasterData } from '@shared/types/publication';
import { cn } from '@shared/utils/utils';
import { useStore } from '@shared/hooks/useStore';
import IconImages from '@shared/assets/images/icons';

interface PublicationFilterSidebarProps {
    categories: MasterData[],
    selectedCategories: string[],
    priceRange: { from: string, to: string },
    onCategoryChange: (categoryCode: string) => void,
    onPriceChange: (field: 'from' | 'to', value: string) => void
}

interface FilterContentProps {
    authStore: any,
    localPrice: { from: string, to: string },
    handleBeforeInputNumber: (e: React.FormEvent<HTMLInputElement>) => void,
    handlePasteNumber: (e: React.ClipboardEvent<HTMLInputElement>) => void,
    handlePriceInputChange: (field: 'from' | 'to', value: string) => void,
    categories: MasterData[],
    onCategoryChange: (categoryCode: string) => void,
    selectedCategories: string[]
}

const FilterContent = ({
    authStore,
    localPrice,
    handleBeforeInputNumber,
    handlePasteNumber,
    handlePriceInputChange,
    categories,
    onCategoryChange,
    selectedCategories
}: FilterContentProps) => (
    <div className='space-y-6'>
        {(!authStore.isLoggedIn || authStore.isStdIndividual) && (
            <>
                <div>
                    <h3 className='font-black text-[18px] leading-[22px] mb-3'>
                        価格
                    </h3>
                    <div className='flex w-full gap-[10px] items-center'>
                        <div className='flex-1 flex flex-col gap-1 min-w-0'>
                            <div className='relative flex items-center'>
                                <span className='absolute left-3 text-sm'>¥</span>
                                <input
                                    type='text'
                                    inputMode='numeric'
                                    maxLength={15}
                                    value={localPrice.from}
                                    onBeforeInput={handleBeforeInputNumber}
                                    onPaste={handlePasteNumber}
                                    onChange={e => handlePriceInputChange('from', e.target.value)}
                                    className='bg-white w-full h-10 rounded-full border border-[#E7E7E7] pl-7 pr-3 py-[6px] leading-[14px] focus:outline-none focus:border-black'
                                />
                            </div>
                        </div>
                        <span className='text-sm leading-[26px]'>~</span>
                        <div className='flex-1 flex flex-col gap-1 min-w-0'>
                            <div className='relative flex items-center'>
                                <span className='absolute left-3 text-sm'>¥</span>
                                <input
                                    type='text'
                                    inputMode='numeric'
                                    maxLength={15}
                                    value={localPrice.to}
                                    onBeforeInput={handleBeforeInputNumber}
                                    onPaste={handlePasteNumber}
                                    onChange={e => handlePriceInputChange('to', e.target.value)}
                                    className='bg-white w-full h-10 rounded-full border border-[#E7E7E7] pl-7 pr-3 py-[6px] leading-[14px] focus:outline-none focus:border-black'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='h-[1px] w-full bg-[#EFEFEF]' />
            </>
        )}

        <div>
            <h3 className='font-black text-[18px] leading-[22px] mb-3'>
                カテゴリ
            </h3>
            <div className='flex flex-col gap-4'>
                {categories.map(category => (
                    <div key={category.id} className='flex items-center gap-3 group cursor-pointer' onClick={() => onCategoryChange(category.code)}>
                        <div className={cn(
                            'w-5 h-5 rounded border flex items-center justify-center transition-colors',
                            selectedCategories.includes(category.code) ? 'bg-primary border-primary' : 'bg-white border-[#E7E7E7] group-hover:border-primary'
                        )}
                        >
                            {selectedCategories.includes(category.code) && (
                                <svg viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='4' strokeLinecap='round' strokeLinejoin='round' className='w-3.5 h-3.5'>
                                    <polyline points='20 6 9 17 4 12' />
                                </svg>
                            )}
                        </div>
                        <span className={cn(
                            'leading-[29px] transition-colors',
                            selectedCategories.includes(category.code) ? 'text-primary' : 'group-hover:text-primary'
                        )}
                        >
                            {category.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const PublicationFilterSidebar = ({
    categories,
    selectedCategories,
    priceRange,
    onCategoryChange,
    onPriceChange
}: PublicationFilterSidebarProps) => {
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const formatNumberString = (value: string) => {
        if (!value) return '';
        const rawValue = value.replace(/,/g, '');
        const numberValue = Number(rawValue);
        return isNaN(numberValue) ? value : numberValue.toLocaleString('en-US');
    };

    const { authStore } = useStore();
    const [localPrice, setLocalPrice] = useState(priceRange);

    useEffect(() => {
        setLocalPrice({
            from: formatNumberString(priceRange.from),
            to: formatNumberString(priceRange.to)
        });
    }, [priceRange]);

    const debouncedOnPriceChangeFrom = useCallback(
        debounce((value: string) => {
            onPriceChange('from', value);
        }, 500),
        [onPriceChange]
    );

    const debouncedOnPriceChangeTo = useCallback(
        debounce((value: string) => {
            onPriceChange('to', value);
        }, 500),
        [onPriceChange]
    );

    const handlePriceInputChange = (field: 'from' | 'to', value: string) => {
        const rawValue = value.replace(/,/g, '');
        if (rawValue.length > 15) return;
        if (rawValue.includes('-')) return;
        if (rawValue !== '' && Number(rawValue) < 0) return;

        const formattedValue = formatNumberString(rawValue);

        setLocalPrice(prev => ({ ...prev, [field]: formattedValue }));
        if (field === 'from') {
            debouncedOnPriceChangeFrom(rawValue);
        } else {
            debouncedOnPriceChangeTo(rawValue);
        }
    };

    const handleBeforeInputNumber = (e: React.FormEvent<HTMLInputElement>) => {
        const { data } = e.nativeEvent as InputEvent;
        if (data && !/^[0-9]+$/.test(data)) {
            e.preventDefault();
        }
    };

    const handlePasteNumber = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const value = e.clipboardData.getData('text');
        if (!/^[0-9,]+$/.test(value)) {
            e.preventDefault();
        }
    };

    const filterProps = {
        authStore,
        localPrice,
        handleBeforeInputNumber,
        handlePasteNumber,
        handlePriceInputChange,
        categories,
        onCategoryChange,
        selectedCategories
    };

    return (
        <>
            <div className='lg:hidden w-full'>
                <button
                    onClick={() => setIsMobileOpen(true)}
                    className='w-full flex items-center justify-center gap-2 h-14 bg-[#F6F6F6] rounded-[10px]'
                >
                    <span className='text-[13px] font-medium leading-[25px]'>絞り込み</span>
                    <img src={IconImages.slidersHorizontalIc} alt='' className='size-6' />
                </button>
            </div>

            {/* Desktop Sidebar */}
            <aside className='hidden lg:block w-[284px] bg-[#F6F6F6] rounded-[10px] p-6 self-start'>
                <FilterContent {...filterProps} />
            </aside>

            {/* Mobile Sidebar Dialog */}
            <Dialog.Root open={isMobileOpen} onOpenChange={setIsMobileOpen}>
                <Dialog.Portal>
                    <Dialog.Overlay className='fixed inset-0 z-50 bg-[#00000026] backdrop-blur-[5.8px] animate-in fade-in duration-300' />
                    <Dialog.Content
                        className='fixed inset-y-0 left-0 z-50 w-full max-w-[335px] bg-[#F6F6F6] animate-in slide-in-from-left duration-300 focus:outline-none flex flex-col'
                    >
                        <div className='flex items-center justify-between px-5 py-4 border-b border-[#EFEFEF]'>
                            <h2 className='text-lg font-black'>絞り込み</h2>
                            <button
                                onClick={() => setIsMobileOpen(false)}
                                className='p-1 hover:bg-gray-100 rounded-full transition-colors'
                            >
                                <X className='w-6 h-6' />
                            </button>
                        </div>
                        <div className='flex-1 overflow-y-auto p-5'>
                            <FilterContent {...filterProps} />
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
};

export default PublicationFilterSidebar;
